import { PostgrestError } from "@supabase/supabase-js";
import { Notification } from "providers/NotifierProvider";
import { supabase } from "supabase/client";
import { Database } from "supabase/types";
import logSentryError from "utils/logSentryError";

type DbProfile = Database["public"]["Tables"]["profiles"];
export type ProfileRow = DbProfile["Row"];
export type ProfileInsert = DbProfile["Insert"];
export type ProfileUpdate = DbProfile["Update"];

export interface GetUserById extends Pick<ProfileRow, "name" | "avatar" | "tag"> {
  uid: number;
}

class ProfileApi {
  showNotification?: (notification: Notification) => void;

  constructor(showNotification?: (notification: Notification) => void) {
    this.showNotification = showNotification;
  }

  /**
   * Get all user names and ids which corresponds to the given [userIds] array
   *
   * @param usersIds array of ids to look for
   * @returns
   */
  async getUsersByIds(usersIds: number[]): Promise<{ users: GetUserById[]; error: PostgrestError | null }> {
    const { data, error } = await supabase
      .from("profiles")
      .select("id, name, avatar, tag")
      .in("id", usersIds)
      .order("name");

    if (error) {
      if (this.showNotification) {
        this.showNotification({
          message: "Unable to load users.",
          severity: "error",
        });
      }

      logSentryError(error, "PROFILE_API: getUsersByIds");
      return { users: [], error };
    }

    const mappedData = data?.map((user) => ({
      uid: user.id,
      name: user.name,
      avatar: user.avatar,
      tag: user.tag,
    }));

    return { users: mappedData, error: null };
  }

  /**
   * Get the profile of the user
   *
   * @param userId Unique id of the use
   * @returns
   */
  async getUserProfile(userId: number): Promise<ProfileRow | null> {
    const { data, error } = await supabase.from("profiles").select("*").eq("id", userId).single();

    if (error) {
      if (this.showNotification) {
        this.showNotification({
          message: "Unable to load user profile.",
          severity: "error",
        });
      }

      logSentryError(error, "PROFILE_API: getUserProfile");
      return null;
    }

    if (data) {
      return data;
    }

    if (!data && this.showNotification) {
      this.showNotification({
        message: "No user profile was found for your account.",
        severity: "error",
      });
      return null;
    }

    return null;
  }

  /**
   * Get users by their name or tag
   *
   * @param tag
   * @returns
   */
  async searchUsersProfiles(tag: string, abSignal: AbortSignal, friendsIds: number[]): Promise<ProfileRow[] | null> {
    const { data, error } = await supabase
      .from("profiles")
      .select("*")
      .not("id", "in", `(${friendsIds})`)
      .ilike("tag", `%${tag}%`)
      .order("name")
      .limit(20)
      .abortSignal(abSignal);

    if (error && parseInt(error.code) !== 20) {
      if (this.showNotification) {
        this.showNotification({
          message: "Unable to load user profiles.",
          severity: "error",
        });
      }

      logSentryError(error, "PROFILE_API: searchUsersProfiles");
      return null;
    }

    if (data) {
      return data;
    }

    return null;
  }

  /**
   * Get all tags which match the provided [lowerCaseName]
   *
   * @param lowerCaseName
   * @returns
   */
  async getTagsByName(lowerCaseName: string): Promise<{
    tags: string[];
    error: PostgrestError | null;
  }> {
    const { data, error } = await supabase.from("profiles").select("tag").eq("lower_case_name", lowerCaseName);

    if (error) {
      logSentryError(error, "PROFILE_API: getTagsByName");
      return { tags: [], error };
    }

    return {
      tags: data ? data.map((row) => row.tag ?? "") : [],
      error: null,
    };
  }

  /**
   * Create a new user profile
   *
   * @param user
   * @returns
   */
  async setupUserProfile(user: {
    uuid: string;
    name: string;
    lower_case_name: string;
    tag: string;
    avatar?: string;
  }): Promise<{ error: PostgrestError | null }> {
    const { error } = await supabase.from("profiles").update(user).eq("uuid", user.uuid);

    if (error) {
      logSentryError(error, "PROFILE_API: setupUserProfile");
      return { error };
    }

    return { error: null };
  }

  /**
   * Update user name
   *
   * @param userId
   * @param updatedData
   * @returns
   */
  async updateUserName(
    userId: number,
    updatedData: { name: string; lowerCaseName: string; tag: string }
  ): Promise<{ profile: ProfileRow | null; error: PostgrestError | null }> {
    const { data, error } = await supabase
      .from("profiles")
      .update({
        name: updatedData.name,
        lower_case_name: updatedData.lowerCaseName,
        tag: updatedData.tag,
      })
      .eq("id", userId)
      .select()
      .single();

    if (error) {
      if (this.showNotification) {
        this.showNotification({
          message: "Unable to update user name.",
          severity: "error",
        });
      }

      logSentryError(error, "PROFILE_API: updateUserName");
      return { profile: null, error };
    }

    return { profile: data, error: null };
  }

  /**
   * Update user profile image
   *
   * @param userId
   * @param imageName
   * @returns
   */
  async updateProfileImage(userId: number, imageName: string): Promise<{ error: PostgrestError | null }> {
    const { error } = await supabase
      .from("profiles")
      .update({
        avatar: imageName,
      })
      .eq("id", userId)
      .single();

    if (error) {
      if (this.showNotification) {
        this.showNotification({
          message: "Unable to update profile image.",
          severity: "error",
        });
      }

      logSentryError(error, "PROFILE_API: updateProfileImage");
      return { error };
    }

    return { error: null };
  }

  /**
   * Get a user by his tag
   *
   * @param tag
   * @returns
   */
  async getUserByTag(tag: string): Promise<{ sentToUserId: number | null; error: PostgrestError | null }> {
    const { error, data } = await supabase.from("profiles").select("id").eq("tag", tag);

    if (error) {
      if (this.showNotification) {
        this.showNotification({
          message: "Unable to load user by tag",
          severity: "error",
        });
      }

      logSentryError(error, "PROFILE_API: getUserByTag");
      return { sentToUserId: null, error };
    }

    if (data && data.length < 1) {
      if (this.showNotification) {
        this.showNotification({
          message: `Unable to find '${tag}' tag.`,
          severity: "warning",
        });
      }

      return { sentToUserId: null, error: null };
    }

    if (!data) {
      return { sentToUserId: null, error: null };
    }

    return {
      sentToUserId: data[0].id,
      error: null,
    };
  }
}

export default ProfileApi;
